// Session context — owns the "who is signed in?" state for the app.
//
// v1 has one concept of auth: a browser cookie issued by Better Auth.
// The Worker's /api/session endpoint is the source of truth for
// whether the cookie is still valid and who it belongs to.
//
// Impersonation ("view as") layers cleanly on top of session:
//
//   * `user`            — the EFFECTIVE user. When an impersonation
//                         session is active, this is the target user
//                         (Brandon, Joy, etc.). Dashboards, sidebars,
//                         role-visibility checks, and scoped data
//                         queries all consume this.
//   * `realUser`        — the actual signed-in human. When not
//                         impersonating, realUser === user (same
//                         object). When impersonating, realUser is
//                         Grant / Baker and user is the target. The
//                         TopNav avatar + user menu read realUser so
//                         Grant never forgets he's impersonating.
//   * `impersonation`   — non-null while a session is active. Used by
//                         the banner component to render the "VIEWING
//                         AS ..." strip at the top of every page.
//   * `stopImpersonation` — imperative action that closes the active
//                           session and re-fetches /api/session.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
} from "react";

import {
  api,
  setUnauthorizedHandler,
  type SessionImpersonationContext,
  type SessionUser,
} from "./api";
import { resetStewNudges } from "./hooks/useStewNudges";
import { checkSessionWithRetry } from "./session-expiry";

export type SessionStatus = "loading" | "signed_out" | "signed_in";

interface SessionContextValue {
  status: SessionStatus;
  // Effective user — rendered perspective of the app.
  user: SessionUser | null;
  // Real signed-in human. Equals `user` when not impersonating.
  realUser: SessionUser | null;
  // True when an impersonation session is active.
  isImpersonating: boolean;
  // Populated when isImpersonating is true.
  impersonation: SessionImpersonationContext | null;
  // Imperative sign-out — clears the server session and local state.
  signOut: () => Promise<void>;
  // Imperative stop-impersonation — closes the active session on the
  // server and re-fetches /api/session so the UI re-renders as the
  // real user.
  stopImpersonation: () => Promise<void>;
  // Force a session refetch — called after sign-in completes so the
  // router transitions from signed_out to signed_in.
  refresh: () => Promise<void>;
  // True when the most recent signed_out transition was caused by an
  // expired/invalid session (a 401 from an authenticated API call),
  // rather than a deliberate sign-out. RequireAuth reads this to show a
  // "your session expired" notice on the sign-in bounce. Cleared on the
  // next successful sign-in or deliberate sign-out.
  sessionExpired: boolean;
}

const SessionContext = createContext<SessionContextValue | null>(null);

export function SessionProvider({ children }: PropsWithChildren) {
  const [status, setStatus] = useState<SessionStatus>("loading");
  const [user, setUser] = useState<SessionUser | null>(null);
  const [realUser, setRealUser] = useState<SessionUser | null>(null);
  const [impersonation, setImpersonation] =
    useState<SessionImpersonationContext | null>(null);
  const [sessionExpired, setSessionExpired] = useState(false);

  // Mirror `status` into a ref so the (stable) unauthorized handler can
  // read the current status without being re-created on every change.
  const statusRef = useRef<SessionStatus>(status);
  useEffect(() => {
    statusRef.current = status;
  }, [status]);

  // Bumped by every refresh and every deliberate sign-out, so a check still
  // waiting out a database blip can't overwrite a newer answer.
  const sessionCheckGeneration = useRef(0);

  const refresh = useCallback(async () => {
    const generation = ++sessionCheckGeneration.current;
    // A failure to GET an answer (network, 5xx: e.g. the 2026-09-30
    // database reset) is retried for about a minute behind the current
    // screen instead of bouncing a still-valid session to sign-in. Only
    // an answer of "no user", or a failure that outlasts the retries,
    // signs the person out. See session-expiry.ts.
    const outcome = await checkSessionWithRetry(() => api.getSession(), {
      sleep: (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
      isSuperseded: () => generation !== sessionCheckGeneration.current,
    });
    if (outcome.kind === "superseded") return;
    try {
      if (outcome.kind === "failed") throw outcome.error;
      const session = outcome.value;
      if (session.user) {
        setUser(session.user);
        // When the server response doesn't include a `real_user`
        // block, the effective user IS the real user (no
        // impersonation active). Mirror the effective user into the
        // realUser slot so `realUser` is never null for a signed-in
        // session — this keeps callers from having to null-check.
        setRealUser(session.real_user ?? session.user);
        setImpersonation(session.impersonation ?? null);
        setStatus("signed_in");
        // A fresh valid session clears any prior expiry flag so the
        // notice doesn't linger past the next successful sign-in.
        setSessionExpired(false);
      } else {
        setUser(null);
        setRealUser(null);
        setImpersonation(null);
        setStatus("signed_out");
      }
    } catch (err) {
      // Still unreachable after the retries — treat as signed-out so the
      // UI falls back to the sign-in page rather than spinning forever.
      console.error("Session check failed:", err);
      setUser(null);
      setRealUser(null);
      setImpersonation(null);
      setStatus("signed_out");
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Stale-state audit fix (2026-05-29) — flush the module-scoped Stew
  // nudge cache on every effective-user transition. The cache survives
  // SPA navigation and the SPA-only signOut (neither reloads the page),
  // so without this a same-tab user switch (sign-out→sign-in, or
  // start/stop impersonation) leaves the PRIOR user's nudges — which
  // reference specific households (regulated PII) — visible in the
  // TopNav badge + Stew banner until the 5-min poll. Keying on the
  // effective-user id covers all four transitions in one place;
  // resetStewNudges() also kicks an immediate refetch for the new
  // user when consumers are mounted.
  useEffect(() => {
    resetStewNudges();
  }, [user?.id]);

  // Global expired-session handler. Any authenticated API 401 (idle
  // timeout, the session window lapsing, an admin revoke) routes here
  // via api.request(). We flip to signed_out + flag the expiry so
  // RequireAuth bounces to /sign-in with a friendly notice and the
  // current location preserved. Guarded on statusRef so a stray 401
  // while already signed_out is a no-op (and so a deliberate sign-out
  // followed by a late 401 doesn't resurrect the expiry notice).
  const handleUnauthorized = useCallback(() => {
    if (statusRef.current !== "signed_in") return;
    setSessionExpired(true);
    setUser(null);
    setRealUser(null);
    setImpersonation(null);
    setStatus("signed_out");
  }, []);

  useEffect(() => {
    setUnauthorizedHandler(handleUnauthorized);
    return () => setUnauthorizedHandler(null);
  }, [handleUnauthorized]);

  const signOut = useCallback(async () => {
    // A session check still retrying must not sign the person back in.
    sessionCheckGeneration.current++;
    try {
      // If the user is currently impersonating, close that session
      // first so the server-side row is tidy. A best-effort call —
      // if it fails, the regular signOut still clears the cookie.
      try {
        await api.stopImpersonation();
      } catch (err) {
        console.error("Stop-impersonation on sign-out failed:", err);
      }
      await api.signOut();
    } catch (err) {
      console.error("Sign-out failed:", err);
    } finally {
      setUser(null);
      setRealUser(null);
      setImpersonation(null);
      setStatus("signed_out");
      // Deliberate sign-out is not an expiry — clear the flag so the
      // sign-in page doesn't show the "session expired" notice.
      setSessionExpired(false);
    }
  }, []);

  const stopImpersonation = useCallback(async () => {
    try {
      await api.stopImpersonation();
    } catch (err) {
      // Surface the failure so the caller can display a toast; we
      // still refetch the session so the UI reflects whatever the
      // server now thinks.
      console.error("Stop-impersonation failed:", err);
    }
    await refresh();
  }, [refresh]);

  const value = useMemo<SessionContextValue>(
    () => ({
      status,
      user,
      realUser,
      isImpersonating: impersonation !== null,
      impersonation,
      signOut,
      stopImpersonation,
      refresh,
      sessionExpired,
    }),
    [
      status,
      user,
      realUser,
      impersonation,
      signOut,
      stopImpersonation,
      refresh,
      sessionExpired,
    ],
  );

  return (
    <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
  );
}

export function useSession(): SessionContextValue {
  const ctx = useContext(SessionContext);
  if (!ctx) {
    throw new Error("useSession must be used inside <SessionProvider>");
  }
  return ctx;
}

/** The session when one is mounted, null otherwise. For components that
 *  only TAILOR what they show to the viewer (the automation trigger
 *  picker hiding a Mortgage-only trigger from other companies) and must
 *  still render in isolation, where a missing provider is not an error. */
export function useOptionalSession(): SessionContextValue | null {
  return useContext(SessionContext);
}
